import { create } from "zustand";
import { persist, subscribeWithSelector } from "zustand/middleware";
import { useAccountStore } from "./account";
import {
  buildDiscoveryLookup,
  checkNodeDiscovered,
  coordsMatch,
  removeDiscoveredMatches,
  type DiscoverMode,
} from "./coordinates";
import { withStorageDOMEvents } from "./dom";
import { isPreviewFeature } from "./preview-release";
import {
  apiDeleteFilter,
  apiListFilters,
  apiPutFilter,
  FiltersApiError,
  serverFilterToLocal,
} from "./filters-api";
import {
  cleanProfileSettingsForFlatten as cleanProfileSettingsForFlattenWith,
  reconcileRehydratedProfiles,
  stripTombstonedFromProfiles as stripTombstonedFromProfilesWith,
} from "./settings-rehydrate";
import {
  applyFilterPatch,
  removeFiltersMatching,
  removeNodeFromFilters,
} from "./filters-mutations";
import {
  adoptLocalFilters,
  pendingIdsWithSyncGrace,
  planFilterHydrate,
} from "./filters-sync";
import {
  clearFilterDirty,
  clearFilterTombstones,
  clearHydrateDrops,
  enqueueFilterDelete,
  flushFilterDeletes,
  getDirtyFilterIds,
  isFilterTombstoned,
  isRecentHydrateDrop,
  loadRecentSyncs,
  recordFilterDirty,
  recordFilterTombstone,
  recordHydrateDrops,
  recordRecentSync,
} from "./filter-tombstones";
import { bindPresetToMap, dropPresetBindings } from "./filter-presets";
import { getAppIdFromPathname, getCurrentGameId } from "./games";
import { isEmbedPath } from "./embed";

export type LiveMode = "static" | "live" | "combined";
/** Which map window a rotate-with-player toggle addresses. */
export type RotateMapSurface = "overlay" | "desktop";

export const LIVE_MODE_VALUES: readonly LiveMode[] = [
  "static",
  "combined",
  "live",
] as const;

/**
 * Live modes gated behind the preview-release (Elite) perk, derived from the
 * PREVIEW_FEATURES registry (`live-mode:<mode>` ids, preview-release.ts).
 * Currently empty — `combined` is public. Add `live-mode:combined` to the
 * registry to gate it again; every consumer (global control, per-filter
 * override, mode resolution) reads this set.
 */
export const PREVIEW_LIVE_MODES: ReadonlySet<LiveMode> = new Set<LiveMode>(
  LIVE_MODE_VALUES.filter((mode) => isPreviewFeature(`live-mode:${mode}`)),
);

export function isLiveReadingActive(liveMode: LiveMode): boolean {
  return liveMode !== "static";
}

export function nextLiveMode(current: LiveMode): LiveMode {
  const idx = LIVE_MODE_VALUES.indexOf(current);
  return LIVE_MODE_VALUES[(idx + 1) % LIVE_MODE_VALUES.length];
}

/**
 * Read the live mode the user is currently entitled to.
 * Silently downgrades preview-gated modes ('combined') to 'live' for
 * users without preview access — keeps the heavy combined-mode render
 * path off the free tier even if their stored setting is 'combined'
 * (legacy or auto-set by peer sync).
 */
/**
 * Pure variant of {@link useEffectiveLiveMode} for non-React call sites
 * (e.g. the imperative live-actor pipeline). Applies the same preview gate.
 */
export function getEffectiveLiveMode(
  liveMode: LiveMode,
  hasPreview: boolean,
): LiveMode {
  if (!hasPreview && PREVIEW_LIVE_MODES.has(liveMode)) return "live";
  return liveMode;
}

export function useEffectiveLiveMode(): LiveMode {
  const liveMode = useSettingsStore((s) => s.liveMode);
  const hasPreview = useAccountStore((s) => s.perks.previewReleaseAccess);
  return getEffectiveLiveMode(liveMode, hasPreview);
}

/**
 * Resolve the live mode for a single filter type, honoring the per-filter
 * override (`liveModeByFilter`) on top of the global effective mode.
 *
 * - When live reading is globally off (`static`), every type stays predicted —
 *   the live plugin isn't running, so per-filter overrides can't show live data.
 * - Otherwise the per-filter override wins, falling back to the global mode.
 * - A resolved `combined` is preview-gated, downgrading to `live` without access
 *   (same rule as the global control).
 */
export function resolveLiveModeForType(
  type: string,
  globalEffectiveMode: LiveMode,
  liveModeByFilter: Record<string, LiveMode> | undefined,
  hasPreview: boolean,
): LiveMode {
  if (globalEffectiveMode === "static") return "static";
  const mode = liveModeByFilter?.[type] ?? globalEffectiveMode;
  if (!hasPreview && PREVIEW_LIVE_MODES.has(mode)) return "live";
  return mode;
}

export type PrivateNode = {
  id: string;
  name?: string;
  description?: string;
  icon: {
    name: string;
    url: string;
    filterId?: string;
    x?: number;
    y?: number;
    width?: number;
    height?: number;
  } | null;
  color?: string;
  radius: number;
  p: [number, number];
  mapName: string;
};

// The reusable style of a private node (everything except its position and id).
// A capped list of the most recent ones is persisted so the "Add Node" dialog
// can offer them as one-click starting points when creating new nodes.
export const MAX_RECENT_PRIVATE_NODE_STYLES = 10;
export type PrivateNodeStyle = {
  filter?: string;
  name?: string;
  description?: string;
  icon: PrivateNode["icon"];
  color?: string;
  radius: number;
};

export type Drawing = {
  id: string;
  polylines?: {
    positions: [number, number][];
    size: number;
    color: string;
    mapName: string;
  }[];
  rectangles?: {
    positions: [number, number][];
    size: number;
    color: string;
    fillColor?: string;
    mapName: string;
  }[];
  polygons?: {
    positions: [number, number][];
    size: number;
    color: string;
    fillColor?: string;
    mapName: string;
  }[];
  circles?: {
    center: [number, number];
    radius: number;
    size: number;
    color: string;
    fillColor?: string;
    mapName: string;
  }[];
  texts?: {
    position: [number, number];
    text: string;
    size: number;
    color: string;
    mapName: string;
  }[];
};

export type DrawingsAndNodes = {
  name: string;
  // New server-managed fields. Populated for filters synced to the
  // Bunny DB (signed-in users). Anonymous filters have neither.
  id?: string;
  game?: string;
  visibility?: "private" | "public";
  shareCode?: string;
  voteCount?: number;
  commentCount?: number;
  updatedAt?: number;
  // True once this filter's `id` has been *confirmed* to exist on the server
  // (a successful PUT, or the id appearing in a hydrate response). Lets hydrate
  // tell "deleted on another device" (synced + now absent → drop) apart from
  // "first upload never landed" (not synced + absent → keep). See
  // {@link mergeHydratedFilters}. Never sent to the server; local bookkeeping only.
  synced?: boolean;
  // Legacy fields kept so existing localStorage data renders. New code
  // doesn't read them — they're inert after the shared-filters rework.
  isShared?: boolean;
  url?: string;
  nodes?: PrivateNode[];
  drawing?: Drawing;
};

export type ColorBlindMode =
  | "none"
  | "protanopia"
  | "deuteranopia"
  | "tritanopia";

export type LabelMode = "off" | "always" | "inRange" | "hotkey";

/**
 * The overlay minimap's Transparency modes (`mapFilter`), in the order the
 * settings select lists them and the "Cycle Map Transparency" hotkey steps
 * through them.
 */
export const MAP_FILTERS = [
  { value: "none", label: "No Transparency" },
  { value: "greyscale", label: "Greyscale" },
  { value: "colorful", label: "Colorful" },
  { value: "full", label: "Full Transparency" },
] as const;

export function nextMapFilter(mapFilter: string): string {
  const index = MAP_FILTERS.findIndex((f) => f.value === mapFilter);
  return MAP_FILTERS[(index + 1) % MAP_FILTERS.length].value;
}

/** Hotkeys that existing profiles get their default binding for on load. */
const HOTKEYS_WITH_BACKFILLED_DEFAULT = [
  "cycle_map_transparency",
  "cycle_filter_preset",
];

export type MapTransform = {
  borderRadius: string;
  transform: string;
  width: string;
  height: string;
};

export const DEFAULT_PROFILE_SETTINGS: ProfileSettings = {
  hotkeys: {
    toggle_app: "F6",
    zoom_in_app: "F7",
    zoom_out_app: "F8",
    toggle_lock_app: "F9",
    discover_node: "F10",
    // Unbound by default: no free key that is safe across every game.
    undiscover_node: "",
    toggle_live_mode: "F5",
    toggle_overlay_fullscreen: "Shift+F9",
    show_labels: "Shift+F5",
    cycle_map_transparency: "Shift+F7",
    cycle_filter_preset: "Shift+F8",
    // Unbound by default: Elite preview, offered only for some games.
    toggle_compact_overlay: "",
  },
  // Like the Overwolf manifest `passthrough` flag (default false): a hotkey
  // press is NOT sent to the game unless its action is true here. The THGL
  // Overwolf apps pass only Discover Node through, so does the app.
  hotkeyPassthrough: { discover_node: true },
  groupName: "",
  // Default to pure live (predicted hidden). Combined (predicted + live) is
  // opt-in. Before Combined was public it was downgraded to live for non-elite
  // users anyway, so this keeps the out-of-box experience consistent.
  liveMode: "live",
  overlayMode: null,
  windowMode: null,
  overlayFullscreen: false,
  compactOverlay: false,
  compactOverlayTransform: null,
  discordProgressGroup: null,
  lockedWindow: false,
  colorBlindMode: "none",
  colorBlindSeverity: 1,
  highContrastMode: false,
  highContrastColor: "#FFFFFFCC",
  highContrastThickness: 2,
  transforms: {},
  mapTransform: null,
  mapFilter: "none",
  mapDarkness: 0,
  windowOpacity: 1,
  discoveredNodes: [],
  hideDiscoveredNodes: false,
  autoDiscoveredNodes: [],
  autoDiscoverCollected: true,
  autoResetRespawned: true,
  flashDespawningNodes: true,
  palCaptureBadges: true,
  palCaptureOnlyIncomplete: false,
  palCaptureCounts: null,
  actorsPollingRate: 100,
  showTraceLine: true,
  followPlayer: true,
  // Turn the map so the player's facing direction is up (needs followPlayer).
  // Two surfaces: the desktop/second-screen window and the website share one
  // value, the in-game overlay minimap has its own (a rotating minimap is
  // wanted in-game far more often than on a big desktop map).
  rotateMapWithPlayer: false,
  rotateMapWithPlayerOverlay: false,
  // Ctrl + left-drag rotates and tilts the map (same as a middle-button drag).
  // Off = Ctrl is ignored and the drag pans, for people whose push-to-talk key
  // is Ctrl. Default keeps the long-standing behaviour.
  rotateMapWithCtrlDrag: true,
  traceLineLength: 100,
  traceLineRate: 5,
  traceLineColor: "#1ccdd1B3",
  traceLineStyle: "dots" as "dots" | "line",
  audioAlertsMuted: false,
  audioAlertNotifications: false,
  audioAlertRange: 5000,
  audioAlertSound: "chime" as const,
  audioAlertVolume: 0.5,
  // Accessibility: the alert's volume follows the distance to the nearest
  // marker of that type and it re-pings while you approach (instead of a
  // single ding on entering range). Off = the long-standing behaviour.
  audioAlertPositional: false,
  showAudioAlertRange: false,
  audioAlertByFilter: {},
  // Per-filter tone override. An absent key = the global audioAlertSound, so
  // nobody who never opens the picker hears a change.
  audioAlertSoundByFilter: {},
  // Palia clock: event ids whose start plays the alert sound (see palia-clock.tsx).
  paliaEventAlerts: {},
  // Real minutes before an event's start the clock alert fires (plus at the start).
  paliaEventAlertLeadMinutes: 1,
  // Also read the alert aloud (browser speech synthesis).
  paliaEventAlertsSpoken: false,
  labelModeByFilter: {},
  liveModeByFilter: {},
  discoverModeByFilter: {},
  hideOverlayByMap: {},
  hideOverlayWithoutMap: true,
  labelTextSize: 1,
  showLabelsHotkey: "l",
  displayDiscordActivityStatus: true,
  // Palia: mute the "a player wants to join your world" join-code-request toast.
  worldCodeRequestsMuted: false,
  presets: {},
  presetByMap: {},
  tempPrivateNode: null,
  recentPrivateNodeStyles: [],
  tempPrivateDrawing: null,
  drawingColor: "#FFFFFFAA",
  drawingFillColor: "#FFFFFF33",
  drawingSize: 4,
  textColor: "#1ccdd1",
  textSize: 20,
  baseIconSize: 1,
  dynamicIconSize: true,
  dynamicIconSizeFactor: 0.2,
  playerIconSize: 1,
  heightArrowSize: 1,
  playerIconColor: "",
  playerBelowMarkers: false,
  iconSizeByGroup: {},
  iconSizeByFilter: {},
  fitBoundsOnChange: false,
  myFilters: [],
  showGrid: false,
  gridLabelSize: 1,
  showFilters: true,
  showInteriorLabelNames: true,
  // Peer Link / Mesh settings
  peerCode: "",
  lastMeSenderId: "",
  playerName: "",
  playerColor: "#38bdf8",
  showPeerLabels: true,
  autoJoinPeer: false,
  autoLiveModeWithMe: true,
};

export const DEFAULT_PROFILE = {
  id: "default",
  name: "Default",
  settings: DEFAULT_PROFILE_SETTINGS,
};

// A saved preset. Originally just an array of active filter ids; now each
// category is captured independently — presence of a field means "this preset
// controls it". So a preset can be filters-only, alerts-only, or any mix, and
// applying it restores exactly the captured categories (others are left alone).
// The icon-size trio (baseIconSize/byGroup/byFilter) is captured as one unit;
// `iconSizeByGroup !== undefined` is the canonical "sizes captured" signal.
// The bare-array form is still accepted (legacy presets = filters only).
export type FilterPreset = {
  filters?: string[];
  baseIconSize?: number;
  iconSizeByGroup?: Record<string, number>;
  iconSizeByFilter?: Record<string, number>;
  audioAlertByFilter?: Record<string, boolean>;
};

// Which app window(s) to show in the Overwolf apps. Mirrors the THGLApp
// window mode (thgl-app/apps.ts). `both` keeps the in-game overlay AND the
// desktop (2nd screen) window open at once. `null` = auto-detect on first run.
export type OverlayWindowMode = "overlay" | "desktop" | "both";

export type ProfileSettings = {
  hotkeys: Record<string, string>;
  /** Per hotkey action: true = the key also reaches the game (THGLApp). */
  hotkeyPassthrough: Record<string, boolean>;
  groupName: string;
  liveMode: LiveMode;
  // Legacy binary preference, kept as a mirror of `windowMode` for backward
  // compat and cosmetic consumers (overlay bg transparency, clock position).
  overlayMode: boolean | null;
  // Source of truth for which window(s) to open. Falls back to `overlayMode`
  // when unset (older profiles), then to monitor-count auto-detection.
  windowMode: OverlayWindowMode | null;
  overlayFullscreen: boolean;
  /**
   * Overlay "Widgets Only" mode: the map is replaced by a small movable panel
   * with the game's `compactOverlay` widgets. Profiles saved before it existed
   * lack the key (read as false). See compact-overlay.ts.
   */
  compactOverlay: boolean;
  /** CSS transform (translate) of the Widgets Only panel; null = default spot. */
  compactOverlayTransform: string | null;
  /**
   * Companion App Discord Rich Presence: the filter group whose discovered
   * count is shown ("12 / 200 Chests found"). null (or missing in older
   * profiles) = auto, the group with the most discovered nodes; "none" = no
   * progress line. See discord-presence-hints.tsx.
   */
  discordProgressGroup: string | null;
  lockedWindow: boolean;
  colorBlindMode: ColorBlindMode;
  colorBlindSeverity: number;
  highContrastMode: boolean;
  highContrastColor: string;
  highContrastThickness: number;
  transforms: Record<string, string>;
  mapTransform: MapTransform | null;
  mapFilter: string;
  // "Dark Map" slider 0..1 (0 = off): darkens the map image on web, desktop
  // and overlay. Profiles saved before it existed lack the key (read as 0).
  mapDarkness: number;
  windowOpacity: number;
  discoveredNodes: string[];
  hideDiscoveredNodes: boolean;
  // Nodes auto-marked discovered from live memory (a subset of discoveredNodes),
  // tracked separately so the UI can flag them as auto-discovered and so opting
  // out can undo exactly those without touching manually-discovered nodes.
  autoDiscoveredNodes: string[];
  // User opt-out for memory-driven auto-discovery of collected items.
  autoDiscoverCollected: boolean;
  // Undiscover a respawning node (ore, trees, forage — not one-time finds like
  // chests) when the live app sees it spawn again at a discovered spot.
  autoResetRespawned: boolean;
  // Blink live markers whose despawn countdown is running (actor.despawnAt), like
  // the game flashes a node in its last 30 s (faster in the last 10 s).
  flashDespawningNodes: boolean;
  // Palworld catch bonus (inbox #112): an "x/5" badge on Pal markers and an
  // optional filter that hides species already caught 5 times. The counts are the
  // last ones the Companion App read ({palId: count}), kept after the game closes.
  palCaptureBadges: boolean;
  palCaptureOnlyIncomplete: boolean;
  palCaptureCounts: Record<string, number> | null;
  actorsPollingRate: number;
  showTraceLine: boolean;
  followPlayer: boolean;
  rotateMapWithPlayer: boolean;
  rotateMapWithPlayerOverlay: boolean;
  rotateMapWithCtrlDrag: boolean;
  traceLineLength: number;
  traceLineRate: number;
  traceLineColor: string;
  traceLineStyle: "dots" | "line";
  audioAlertsMuted: boolean;
  audioAlertNotifications: boolean;
  audioAlertRange: number;
  audioAlertSound: "chime" | "ping" | "beacon" | "soft";
  audioAlertVolume: number;
  audioAlertPositional: boolean;
  showAudioAlertRange: boolean;
  audioAlertByFilter: Record<string, boolean>;
  /**
   * Per-filter alert tone. Absent key = the global `audioAlertSound`.
   * The union is spelled out by hand, like `audioAlertSound` above: @repo/lib
   * must not import from @repo/ui, where `AudioAlertSound` is declared.
   */
  audioAlertSoundByFilter: Record<string, "chime" | "ping" | "beacon" | "soft">;
  paliaEventAlerts: Record<string, boolean>;
  paliaEventAlertLeadMinutes: number;
  paliaEventAlertsSpoken: boolean;
  labelModeByFilter: Record<string, LabelMode>;
  /**
   * Per-filter live-mode override. Absent key = inherit the global live mode.
   * Lets a user keep predictions for some filters (e.g. campsites) while
   * showing others live-only (e.g. bugs/mining). See {@link resolveLiveModeForType}.
   */
  liveModeByFilter: Record<string, LiveMode>;
  /**
   * Per-filter override for the "Discover Nearest Node" hotkey. Absent key =
   * inherit the static-flag-derived default (fixed types `enabled`, live/dynamic
   * types `predicted`). See {@link resolveDiscoverMode}.
   */
  discoverModeByFilter: Record<string, DiscoverMode>;
  /**
   * Maps on which the overlay auto-hides while the PLAYER is on them (key =
   * mapName, only `true` entries stored). Hides the overlay map/filters only —
   * the ad container and header are untouched. See useOverlayMapHidden().
   */
  hideOverlayByMap: Record<string, boolean>;
  /**
   * Auto-hide the overlay while NO map is detected (main menu, character
   * select). Default ON. The "Show Map" pill / fullscreen hotkey shows it
   * temporarily until a map is detected. See useOverlayMapHidden().
   */
  hideOverlayWithoutMap: boolean;
  labelTextSize: number;
  showLabelsHotkey: string;
  displayDiscordActivityStatus: boolean;
  worldCodeRequestsMuted: boolean;
  presets: Record<string, string[] | FilterPreset>;
  /**
   * Per-map preset auto-apply (key = top-level mapName, value = preset name).
   * One preset per map; switching to a bound map applies that preset like a
   * manual selection. See resolveAutoApplyPreset() in filter-presets.ts.
   */
  presetByMap: Record<string, string>;
  tempPrivateNode: (Partial<PrivateNode> & { filter?: string }) | null;
  recentPrivateNodeStyles: PrivateNodeStyle[];
  tempPrivateDrawing: (Partial<Drawing> & { name?: string }) | null;
  drawingColor: string;
  drawingFillColor: string;
  drawingSize: number;
  textColor: string;
  textSize: number;
  baseIconSize: number;
  dynamicIconSize: boolean;
  dynamicIconSizeFactor: number;
  playerIconSize: number;
  // Size of the height arrows (▲/▼) on markers relative to the icon, so they
  // stay readable with small icons. 1 = default.
  heightArrowSize: number;
  // Recolor the player icon ("#rrggbb"); "" keeps the game's own colors.
  playerIconColor: string;
  // Draw the player icon underneath the map markers instead of on top
  // (nearby markers, e.g. fish while fishing, stay readable).
  playerBelowMarkers: boolean;
  iconSizeByGroup: Record<string, number>;
  iconSizeByFilter: Record<string, number>;
  fitBoundsOnChange: boolean;
  myFilters: DrawingsAndNodes[];
  showGrid: boolean;
  /** Scale factor for the map grid's cell labels (A1, B2, ...). */
  gridLabelSize: number;
  showFilters: boolean;
  /**
   * Draw the interior NAME next to the layer button on the map ("Hunter's
   * Den"). Off leaves the button itself — icon plus floor numbers — so the
   * floor maps stay reachable while the map gets much less text on it.
   * Independent of the lock rule, which hides the buttons entirely.
   */
  showInteriorLabelNames: boolean;
  // Peer Link / Mesh settings
  peerCode: string;
  lastMeSenderId: string;
  playerName: string;
  playerColor: string;
  showPeerLabels: boolean;
  autoJoinPeer: boolean;
  autoLiveModeWithMe: boolean;

  // Deprecated
  privateNodes?: PrivateNode[];
  privateDrawings?: Drawing[];
  sharedFilters?: {
    url: string;
    filter: string;
  }[];
};

export interface ProfileActions {
  setHotkey: (key: string, value: string) => void;
  setHotkeys: (hotkeys: Record<string, string>) => void;
  setHotkeyPassthrough: (key: string, passthrough: boolean) => void;
  setGroupName: (groupName: string) => void;
  setLiveMode: (liveMode: LiveMode) => void;
  cycleLiveMode: () => void;
  setOverlayMode: (overlayMode: boolean) => void;
  setWindowMode: (windowMode: OverlayWindowMode) => void;
  toggleOverlayFullscreen: () => void;
  toggleCompactOverlay: () => void;
  setCompactOverlayTransform: (transform: string | null) => void;
  setDiscordProgressGroup: (group: string | null) => void;
  toggleLockedWindow: () => void;
  setColorBlindMode: (mode: ColorBlindMode) => void;
  setColorBlindSeverity: (severity: number) => void;
  toggleHighContrastMode: () => void;
  setHighContrastColor: (color: string) => void;
  setHighContrastThickness: (thickness: number) => void;
  setTransform: (id: string, transform: string) => void;
  setMapTransform: (mapTransform: MapTransform | null) => void;
  setMapFilter: (mapFilter: string) => void;
  cycleMapFilter: () => void;
  setMapDarkness: (mapDarkness: number) => void;
  setWindowOpacity: (windowOpacity: number) => void;
  resetTransform: () => void;
  resetInterface: () => void;
  isDiscoveredNode: (nodeId: string) => boolean;
  // True if the node was auto-discovered from live memory (vs manually marked).
  isAutoDiscoveredNode: (nodeId: string) => boolean;
  toggleDiscoveredNode: (nodeId: string) => void;
  setDiscoverNode: (nodeId: string, discovered: boolean) => void;
  toggleHideDiscoveredNodes: () => void;
  setDiscoveredNodes: (discoveredNodes: string[]) => void;
  // Bulk discover/undiscover (the per-filter "Discover all" button). One store
  // update + persist regardless of count; undiscover uses tolerant matching so
  // legacy-format entries are removed too, and prunes autoDiscoveredNodes to
  // keep it a subset of discoveredNodes.
  setDiscoveredNodesBulk: (nodeIds: string[], discovered: boolean) => void;
  // Mark nodes discovered from live memory — adds to BOTH discoveredNodes (so all
  // discovery logic applies) and autoDiscoveredNodes (so the UI can flag them).
  markAutoDiscovered: (nodeIds: string[]) => void;
  setAutoDiscoverCollected: (enabled: boolean) => void;
  setAutoResetRespawned: (enabled: boolean) => void;
  setFlashDespawningNodes: (enabled: boolean) => void;
  setPalCaptureBadges: (enabled: boolean) => void;
  setPalCaptureOnlyIncomplete: (enabled: boolean) => void;
  setPalCaptureCounts: (counts: Record<string, number> | null) => void;
  setActorsPollingRate: (actorsPollingRate: number) => void;
  toggleShowTraceLine: () => void;
  toggleFollowPlayer: () => void;
  // Heading-up mode only works while following the player, so enabling it
  // switches Follow Player on as well (disabling leaves follow untouched).
  // `surface` picks the value: "overlay" = the in-game overlay minimap,
  // "desktop" = desktop / second-screen window and the website.
  setRotateMapWithPlayer: (enabled: boolean, surface: RotateMapSurface) => void;
  setRotateMapWithCtrlDrag: (enabled: boolean) => void;
  setTraceLineLength: (traceLineLength: number) => void;
  setTraceLineRate: (traceLineRate: number) => void;
  setTraceLineColor: (traceLineColor: string) => void;
  setTraceLineStyle: (style: "dots" | "line") => void;
  toggleAudioAlertsMuted: () => void;
  toggleAudioAlertNotifications: () => void;
  setAudioAlertRange: (range: number) => void;
  setAudioAlertSound: (sound: "chime" | "ping" | "beacon" | "soft") => void;
  setAudioAlertVolume: (volume: number) => void;
  toggleAudioAlertPositional: () => void;
  toggleShowAudioAlertRange: () => void;
  toggleAudioAlertByFilter: (filterId: string) => void;
  togglePaliaEventAlert: (eventId: string) => void;
  setPaliaEventAlertLeadMinutes: (minutes: number) => void;
  togglePaliaEventAlertsSpoken: () => void;
  setAudioAlertByFilters: (filterIds: string[], enabled: boolean) => void;
  // One setter for both cases: the popover passes `[filterId]` for a single
  // filter and the whole group's ids for a group. `"default"` deletes the key
  // so the filter falls back to the global tone.
  setAudioAlertSoundByFilters: (
    filterIds: string[],
    sound: "chime" | "ping" | "beacon" | "soft" | "default",
  ) => void;
  resetAudioAlerts: () => void;
  setLabelModeByFilter: (filterId: string, mode: LabelMode) => void;
  setLabelModeByFilters: (filterIds: string[], mode: LabelMode) => void;
  // `"default"` clears the override so the filter inherits the global live mode.
  setLiveModeByFilter: (filterId: string, mode: LiveMode | "default") => void;
  setLiveModeByFilters: (
    filterIds: string[],
    mode: LiveMode | "default",
  ) => void;
  // Per-filter Discover-Nearest override. `"default"` clears the override so the
  // filter inherits the static-flag-derived default (see resolveDiscoverMode).
  setDiscoverModeByFilter: (
    filterId: string,
    mode: DiscoverMode | "default",
  ) => void;
  setDiscoverModeByFilters: (
    filterIds: string[],
    mode: DiscoverMode | "default",
  ) => void;
  // Flag/unflag a map for overlay auto-hide. `false` deletes the key.
  setHideOverlayOnMap: (mapName: string, hide: boolean) => void;
  setHideOverlayWithoutMap: (hide: boolean) => void;
  setShowInteriorLabelNames: (show: boolean) => void;
  setLabelTextSize: (size: number) => void;
  setShowLabelsHotkey: (key: string) => void;
  setDisplayDiscordActivityStatus: (
    displayDiscordActivityStatus: boolean,
  ) => void;
  setWorldCodeRequestsMuted: (muted: boolean) => void;
  addPreset: (presetName: string, preset: FilterPreset) => void;
  removePreset: (presetName: string) => void;
  // Rebuild the presets map in the given key order (presets render in
  // insertion order). Names not listed are appended, keeping them safe.
  reorderPresets: (orderedNames: string[]) => void;
  // Bind a preset to a map (replaces that map's previous binding) or clear
  // the map's binding with `null`.
  setPresetForMap: (mapName: string, presetName: string | null) => void;
  // Replace icon sizes and/or per-filter audio alerts wholesale (used when
  // applying a preset, so unset entries revert to defaults instead of
  // lingering). Only the provided categories are touched.
  applyPresetSettings: (settings: {
    iconSizes?: {
      baseIconSize: number;
      iconSizeByGroup: Record<string, number>;
      iconSizeByFilter: Record<string, number>;
    };
    audioAlertByFilter?: Record<string, boolean>;
  }) => void;
  setTempPrivateNode: (
    tempPrivateNode: (Partial<PrivateNode> & { filter?: string }) | null,
  ) => void;
  pushRecentPrivateNodeStyle: (style: PrivateNodeStyle) => void;
  setTempPrivateDrawing: (
    tempPrivateDrawing: (Partial<Drawing> & { name?: string }) | null,
  ) => void;
  setDrawingColor: (drawingColor: string) => void;
  setDrawingFillColor: (drawingFillColor: string) => void;
  setDrawingSize: (drawingSize: number) => void;
  setTextColor: (textColor: string) => void;
  setTextSize: (textSize: number) => void;
  setBaseIconSize: (baseIconSize: number) => void;
  toggleDynamicIconSize: () => void;
  setDynamicIconSizeFactor: (factor: number) => void;
  setPlayerIconSize: (playerIconSize: number) => void;
  setHeightArrowSize: (heightArrowSize: number) => void;
  setPlayerIconColor: (playerIconColor: string) => void;
  togglePlayerBelowMarkers: () => void;
  setIconSizeByGroup: (group: string, size: number) => void;
  setIconSizeByFilter: (id: string, size: number) => void;
  toggleFitBoundsOnChange: () => void;
  setMyFilters: (myFilters: DrawingsAndNodes[]) => void;
  setMyFilter: (name: string, myFilter: Partial<DrawingsAndNodes>) => void;
  addMyFilter: (myFilter: DrawingsAndNodes) => void;
  removeMyFilter: (myFilterName: string) => void;
  /**
   * Delete EVERY filter: tombstoned and server-deleted like a single removal,
   * so a reset actually sticks instead of being resurrected by the next
   * hydrate.
   */
  resetMyFilters: () => void;
  removeMyNode: (nodeId: string) => void;
  /**
   * Replace this game's filters in local state with the server's view.
   * Local filters without an `id` (anonymous / not yet synced) are
   * preserved. Called on signed-in mount per game tenant.
   */
  hydrateFiltersFromServer: (game: string) => Promise<void>;
  /**
   * Give a server id to signed-in filters that have content but none, and
   * upload them. Covers backups restored via `setMyFilters` (local-only) and
   * filters healed from a botched import. No-op when signed out.
   */
  adoptLocalOnlyFilters: () => void;
  toggleShowGrid: () => void;
  setGridLabelSize: (gridLabelSize: number) => void;
  toggleShowFilters: () => void;
  // Peer Link / Mesh settings
  setPeerCode: (code: string) => void;
  setLastMeSenderId: (id: string) => void;
  setPlayerName: (name: string) => void;
  setPlayerColor: (color: string) => void;
  setShowPeerLabels: (show: boolean) => void;
  setAutoJoinPeer: (autoJoin: boolean) => void;
  setAutoLiveModeWithMe: (autoLiveMode: boolean) => void;
}

export type Profile = {
  id: string;
  name: string;
  settings: ProfileSettings;
  createdAt: number;
  updatedAt: number;
};

class ProfileManager {
  static createProfileId() {
    return `profile-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  static getDefaultProfile(): Profile {
    const defaultProfile = {
      id: DEFAULT_PROFILE.id,
      name: DEFAULT_PROFILE.name,
      settings: JSON.parse(JSON.stringify(DEFAULT_PROFILE_SETTINGS)),
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };
    return defaultProfile;
  }

  static createProfile(name: string): Profile {
    const newProfile = {
      id: this.createProfileId(),
      name,
      settings: JSON.parse(JSON.stringify(DEFAULT_PROFILE_SETTINGS)),
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };
    return newProfile;
  }

  static duplicateProfile(profile: Profile, name: string): Profile {
    // Deep clone the profile object
    const newProfile: Profile = JSON.parse(JSON.stringify(profile));
    newProfile.name = name;
    newProfile.id = this.createProfileId();
    newProfile.createdAt = Date.now();
    newProfile.updatedAt = Date.now();
    return newProfile;
  }
}

// SettingsStore now has flattened ProfileSettings at root level
export interface SettingsStore extends ProfileSettings, ProfileActions {
  _hasHydrated: boolean;
  setHasHydrated: (state: boolean) => void;

  // Profile Management (only profiles array and currentProfileId are persisted)
  profiles: Profile[];
  currentProfileId: string;
  createProfile: (name: string) => void;
  switchProfile: (profileId: string) => void;
  renameProfile: (profileId: string, newName: string) => void;
  deleteProfile: (profileId: string) => void;
  exportProfile: (profileId: string) => Profile | null;
  importProfile: (profile: Profile) => void;
  duplicateProfile: (profileId: string, name: string) => void;
}

const getStorageName = () => {
  if (typeof window !== "undefined") {
    // Embedded maps (other sites' iframes) never touch the visitor's own
    // map state: same-site iframes would otherwise share this storage.
    if (isEmbedPath(window.location.pathname)) return "thgl-embed-settings";
    // Locale-aware: /{locale}/apps/<id> must map to the SAME storage as
    // /apps/<id>, otherwise non-English languages share the generic fallback
    // while English gets the per-app storage (settings "change" with locale).
    const appId = getAppIdFromPathname(window.location.pathname);
    if (appId) {
      const name = `thgl-settings-${appId}`;
      // One-time seed: non-English users' settings lived in the generic
      // fallback storage until the locale fix — adopt them when the per-app
      // storage doesn't exist yet, so the fix doesn't read as a reset.
      try {
        const generic = localStorage.getItem("settings-storage");
        if (generic !== null && localStorage.getItem(name) === null) {
          localStorage.setItem(name, generic);
        }
      } catch {
        // Storage access can throw (privacy mode) — per-app name still works.
      }
      return name;
    }
  }
  return "settings-storage";
};

const SYNC_DEBOUNCE_MS = 1000;
const syncTimers = new Map<string, ReturnType<typeof setTimeout>>();
// Ids whose PUT has fired and not yet resolved. Together with the queued
// `syncTimers` keys these form the "pending" set that hydrate must not clobber
// (a re-hydrate on focus could otherwise overwrite an edit mid-upload).
const inFlightSyncIds = new Set<string>();
// The debounced PUT body per id, so it can be fired early (page hide) instead
// of waiting out the timer. Kept in step with `syncTimers`.
const pendingSyncRuns = new Map<string, () => void>();
// How long a just-synced id is protected from hydrate's "synced-but-absent =
// deleted elsewhere" drop. Covers the save-then-hydrate race AND a window
// close/reopen in between (the grace is loaded from durable storage, not
// in-memory, so a freshly reopened window still honours it) plus read-replica
// lag. Long enough for the server list to reflect the write; short relative to
// how rarely you'd remote-delete a filter you just created here.
const RECENT_SYNC_GRACE_MS = 60_000;

/**
 * Ids with a queued (debounced) or in-flight PUT, PLUS ids whose PUT landed
 * within the last {@link RECENT_SYNC_GRACE_MS}. The recently-synced stamps are
 * DURABLE (localStorage via {@link loadRecentSyncs}), so this protection
 * survives a window close/reopen — the in-memory pending sets do not.
 */
export function getPendingSyncIds(): Set<string> {
  return pendingIdsWithSyncGrace(
    // Dirty ids are durable, so an edit whose debounce never fired (window
    // closed within the second) still counts as pending in the NEXT session —
    // otherwise hydrate would overwrite it with the older server copy.
    new Set<string>([
      ...syncTimers.keys(),
      ...inFlightSyncIds,
      ...getDirtyFilterIds(),
    ]),
    loadRecentSyncs(),
    Date.now(),
    RECENT_SYNC_GRACE_MS,
  );
}

function isSignedIn(): boolean {
  return !!useAccountStore.getState().decryptedUserId;
}

/**
 * Schedule a debounced PUT for a filter that already has a server `id`.
 * Coalesces rapid edits (drawing nodes, dragging shapes) into one
 * round-trip per second per filter. Anonymous users (no server id) are
 * no-ops — filters stay local.
 */
function scheduleFilterSync(filter: DrawingsAndNodes) {
  if (!filter.id) return;
  if (!isSignedIn()) return;
  const id = filter.id;
  const existing = syncTimers.get(id);
  if (existing) clearTimeout(existing);
  // Durably mark the local copy as ahead of the server BEFORE the debounce, so
  // closing the window inside that second can't strand the edit: without this
  // the next hydrate hands the win to the older server copy. Cleared when the
  // PUT lands. See recordFilterDirty.
  recordFilterDirty(id);
  const run = () => {
    syncTimers.delete(id);
    pendingSyncRuns.delete(id);
    const game = filter.game ?? getCurrentGameId();
    if (!game) {
      console.error("[filter sync] no game id, skipping put", id);
      return;
    }
    inFlightSyncIds.add(id);
    void apiPutFilter(id, {
      game,
      name: filter.name,
      payload: { nodes: filter.nodes, drawing: filter.drawing },
      visibility: filter.visibility ?? "private",
    })
      .then(() => {
        // Remember this PUT just landed (DURABLY): a hydrate whose server-list
        // fetch raced it (or hits replica lag), INCLUDING one on a freshly
        // reopened window, would otherwise see the id as "synced but absent"
        // and drop the just-saved filter. The grace window in
        // getPendingSyncIds protects it until the list catches up.
        recordRecentSync(id);
        // The server now has this edit — the local copy is no longer ahead.
        clearFilterDirty(id);
        // Confirm the id now exists on the server so hydrate can safely
        // drop it if it later disappears (deleted elsewhere) instead of
        // resurrecting it. setMyFilters doesn't re-trigger a sync, so no loop.
        const state = useSettingsStore.getState();
        if (state.myFilters.some((f) => f.id === id && !f.synced)) {
          state.setMyFilters(
            state.myFilters.map((f) =>
              f.id === id ? { ...f, synced: true } : f,
            ),
          );
        }
      })
      .catch((err) => {
        // Account switched on this device (or somehow we have a stale
        // server id we don't own). Strip the local id so the filter
        // visibly drops back to local-only and the user can choose
        // to re-save it under their current account.
        if (err instanceof FiltersApiError && err.status === 403) {
          const state = useSettingsStore.getState();
          const updatedFilters = state.myFilters.map((f) =>
            f.id === id
              ? {
                  ...f,
                  id: undefined,
                  visibility: undefined,
                  shareCode: undefined,
                  voteCount: undefined,
                  commentCount: undefined,
                }
              : f,
          );
          state.setMyFilters(updatedFilters);
          console.warn(
            `[filter sync] ${id} owned by another account; demoting to local-only`,
          );
          return;
        }
        console.error("[filter sync] put failed", id, err);
      })
      .finally(() => {
        inFlightSyncIds.delete(id);
      });
  };
  pendingSyncRuns.set(id, run);
  syncTimers.set(id, setTimeout(run, SYNC_DEBOUNCE_MS));
}

/**
 * Fire every debounced PUT immediately.
 *
 * Called when the page is hidden (tab switch, window close, app shutdown) so a
 * one-second debounce doesn't swallow the user's last edit. The durable dirty
 * marker is the real safety net — this just narrows the window so the common
 * case syncs promptly instead of waiting for the next session's hydrate.
 */
export function flushPendingFilterSyncs(): void {
  for (const [id, timer] of syncTimers) {
    clearTimeout(timer);
    const run = pendingSyncRuns.get(id);
    if (run) run();
  }
}

if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushPendingFilterSyncs();
  });
}

/**
 * Server delete via the persistent pending-delete queue (see
 * filter-tombstones.ts). The id is enqueued even when signed out — the flush
 * skips until a signed-in surface picks it up — so deleting a synced filter
 * while logged out no longer orphans its server row (which every later
 * hydrate would resurrect). Failures are retried from hydrate.
 */
function fireFilterDelete(id: string) {
  // Cancel any pending PUT for this id so we don't race the delete.
  const existing = syncTimers.get(id);
  if (existing) {
    clearTimeout(existing);
    syncTimers.delete(id);
  }
  pendingSyncRuns.delete(id);
  // The filter is going away — a pending "local is ahead" marker would
  // otherwise make the next hydrate try to re-upload a deleted filter.
  clearFilterDirty(id);
  enqueueFilterDelete(id);
  void flushQueuedFilterDeletes();
}

/** Drain the delete queue. 404 = already gone, 403 = not ours: stop retrying. */
function flushQueuedFilterDeletes(): Promise<void> {
  return flushFilterDeletes({
    isSignedIn,
    deleteFilter: apiDeleteFilter,
    shouldDiscard: (err) =>
      err instanceof FiltersApiError &&
      (err.status === 404 || err.status === 403),
  });
}

/**
 * Strip tombstoned filters from every profile snapshot. Applied at the two
 * persistence choke points (partialize on write, merge on rehydrate) so a
 * stale window's whole-blob write can't resurrect a filter deleted in another
 * window — the multi-window last-writer-wins race behind the "custom filters
 * keep coming back" ticket. Returns untouched inputs when no tombstones match
 * (partialize runs on every store write).
 */
/**
 * A profile's settings with tombstoned filters stripped, for the flows that
 * flatten `...profile.settings` straight into live state (switch / import /
 * delete-switch) and would otherwise restore a stale myFilters snapshot.
 * Profiles without a myFilters key are returned as-is (flattening must not
 * clear the root list in that legacy case).
 */
function cleanProfileSettingsForFlatten(
  settings: Profile["settings"],
): Profile["settings"] {
  return cleanProfileSettingsForFlattenWith(settings, isFilterTombstoned);
}

function stripTombstonedFromProfiles(profiles: Profile[]): Profile[] {
  return stripTombstonedFromProfilesWith(profiles, isFilterTombstoned);
}

// Cache for isDiscoveredNode results - invalidated when discoveredNodes changes
let discoveredCache: Map<string, boolean> | null = null;
let discoveryLookup: ReturnType<typeof buildDiscoveryLookup> | null = null;
let cachedDiscoveredNodes: string[] | null = null;
// Parallel cache for isAutoDiscoveredNode (auto-discovered-from-memory subset).
let autoDiscoveredCache: Map<string, boolean> | null = null;
let autoDiscoveryLookup: ReturnType<typeof buildDiscoveryLookup> | null = null;
let cachedAutoDiscoveredNodes: string[] | null = null;

export const useSettingsStore = create(
  subscribeWithSelector(
    persist<SettingsStore>(
      (set, get) => {
        // Helper to update both flat state and profiles array
        const updateSettings = (settings: Partial<ProfileSettings>) => {
          const state = get();
          const updatedProfiles = state.profiles.map((p) =>
            p.id === state.currentProfileId
              ? {
                  ...p,
                  settings: { ...p.settings, ...settings },
                  updatedAt: Date.now(),
                }
              : p,
          );

          set({
            ...settings,
            profiles: updatedProfiles,
          });
        };

        return {
          _hasHydrated: false,
          setHasHydrated: (state) => {
            set({
              _hasHydrated: state,
            });
          },

          // Profile Management
          currentProfileId: DEFAULT_PROFILE.id,
          profiles: [ProfileManager.getDefaultProfile()],

          createProfile: (name: string) => {
            const state = get();
            const newProfile = ProfileManager.createProfile(name);
            set({
              profiles: [...state.profiles, newProfile],
              currentProfileId: newProfile.id,
              ...newProfile.settings, // Flatten new profile settings to root
            });
          },

          switchProfile: (profileId: string) => {
            const state = get();
            const profile = state.profiles.find((p) => p.id === profileId);
            if (!profile) return;

            set({
              currentProfileId: profileId,
              // Flatten switched profile settings to root (minus tombstoned
              // filters — the snapshot may predate deletes made elsewhere)
              ...cleanProfileSettingsForFlatten(profile.settings),
            });
          },

          renameProfile: (profileId: string, newName: string) => {
            set((state) => ({
              profiles: state.profiles.map((p) =>
                p.id === profileId
                  ? { ...p, name: newName, updatedAt: Date.now() }
                  : p,
              ),
            }));
          },

          deleteProfile: (profileId: string) => {
            const state = get();
            if (state.profiles.length <= 1) return; // Don't delete the last profile

            const newProfiles = state.profiles.filter(
              (p) => p.id !== profileId,
            );
            const newCurrentProfileId =
              state.currentProfileId === profileId
                ? newProfiles[0].id
                : state.currentProfileId;

            // If we're switching to a different profile, load its settings
            if (newCurrentProfileId !== state.currentProfileId) {
              const newProfile = newProfiles.find(
                (p) => p.id === newCurrentProfileId,
              );
              if (newProfile) {
                set({
                  profiles: newProfiles,
                  currentProfileId: newCurrentProfileId,
                  // Flatten settings to root (minus tombstoned filters)
                  ...cleanProfileSettingsForFlatten(newProfile.settings),
                });
                return;
              }
            }

            set({
              profiles: newProfiles,
              currentProfileId: newCurrentProfileId,
            });
          },

          exportProfile: (profileId: string) => {
            const state = get();
            const profile = state.profiles.find((p) => p.id === profileId);
            if (!profile) return null;

            // If exporting the current profile, sync flat state into the
            // profile to guard against any desync between root and array.
            if (profileId === state.currentProfileId) {
              const synced: Partial<ProfileSettings> = {};
              for (const key of Object.keys(DEFAULT_PROFILE_SETTINGS)) {
                if (key in state) {
                  (synced as any)[key] = (state as any)[key];
                }
              }
              return {
                ...profile,
                settings: { ...profile.settings, ...synced },
              };
            }

            return profile;
          },

          importProfile: (profile: Profile) => {
            const state = get();
            // Check if profile with same ID already exists
            const exists = state.profiles.some((p) => p.id === profile.id);
            if (exists) {
              // Generate new ID
              profile.id = ProfileManager.createProfileId();
            }
            set({
              profiles: [...state.profiles, profile],
              currentProfileId: profile.id,
              // Flatten imported profile settings to root (minus tombstoned
              // filters — an exported file may contain since-deleted ones)
              ...cleanProfileSettingsForFlatten(profile.settings),
            });
          },

          duplicateProfile: (profileId: string, name: string) => {
            const state = get();
            const profile = state.profiles.find((p) => p.id === profileId);
            if (!profile) return;

            const newProfile = ProfileManager.duplicateProfile(profile, name);

            set({
              profiles: [...state.profiles, newProfile],
              currentProfileId: newProfile.id,
              ...newProfile.settings, // Flatten new profile settings to root
            });
          },

          // Flattened ProfileSettings (initial values from DEFAULT_PROFILE_SETTINGS)
          ...DEFAULT_PROFILE_SETTINGS,

          // Actions for updating settings (update both flat state and profiles array)
          setHotkey: (key, value) => {
            const state = get();
            updateSettings({
              hotkeys: { ...state.hotkeys, [key]: value },
            });
          },

          setHotkeys: (hotkeys) => {
            updateSettings({ hotkeys });
          },

          setHotkeyPassthrough: (key, passthrough) => {
            const state = get();
            updateSettings({
              hotkeyPassthrough: {
                ...state.hotkeyPassthrough,
                [key]: passthrough,
              },
            });
          },

          setGroupName: (groupName) => {
            updateSettings({ groupName });
          },

          setLiveMode: (liveMode) => {
            updateSettings({ liveMode });
          },

          cycleLiveMode: () => {
            const state = get();
            updateSettings({
              liveMode: nextLiveMode(state.liveMode),
            });
          },

          setOverlayMode: (overlayMode) => {
            updateSettings({
              overlayMode,
              windowMode: overlayMode ? "overlay" : "desktop",
            });
          },

          setWindowMode: (windowMode) => {
            updateSettings({
              windowMode,
              // Mirror to the legacy boolean so cosmetic consumers (overlay bg
              // transparency, clock position) stay correct. `both` styles the
              // overlay window as an overlay, so it maps to true.
              overlayMode: windowMode === "desktop" ? false : true,
            });
          },

          toggleOverlayFullscreen: () => {
            const state = get();
            updateSettings({
              overlayFullscreen: !state.overlayFullscreen,
            });
          },

          toggleCompactOverlay: () => {
            const state = get();
            updateSettings({ compactOverlay: !state.compactOverlay });
          },

          setCompactOverlayTransform: (transform) => {
            updateSettings({ compactOverlayTransform: transform });
          },

          setDiscordProgressGroup: (group) => {
            updateSettings({ discordProgressGroup: group });
          },

          toggleLockedWindow: () => {
            const state = get();
            updateSettings({
              lockedWindow: !state.lockedWindow,
            });
          },

          setColorBlindMode: (mode) => {
            updateSettings({ colorBlindMode: mode });
          },

          setColorBlindSeverity: (severity: number) => {
            updateSettings({
              colorBlindSeverity: Math.max(0, Math.min(1, severity)),
            });
          },

          toggleHighContrastMode: () => {
            const state = get();
            updateSettings({
              highContrastMode: !state.highContrastMode,
            });
          },

          setHighContrastColor: (color: string) => {
            updateSettings({ highContrastColor: color });
          },

          setHighContrastThickness: (thickness: number) => {
            updateSettings({
              highContrastThickness: Math.max(1, Math.min(6, thickness)),
            });
          },

          setTransform: (id: string, transform: string) => {
            const state = get();
            updateSettings({
              transforms: {
                ...state.transforms,
                [id]: transform,
              },
            });
          },

          setMapTransform: (mapTransform) => {
            updateSettings({ mapTransform });
          },

          setMapFilter: (mapFilter) => {
            updateSettings({ mapFilter });
          },

          cycleMapFilter: () => {
            updateSettings({ mapFilter: nextMapFilter(get().mapFilter) });
          },

          setMapDarkness: (mapDarkness) => {
            updateSettings({
              mapDarkness: Math.max(0, Math.min(1, mapDarkness)),
            });
          },

          setWindowOpacity: (windowOpacity) => {
            updateSettings({ windowOpacity });
          },

          resetTransform: () => {
            updateSettings({
              transforms: {},
              mapTransform: null,
              playerIconSize: 1,
              heightArrowSize: 1,
              baseIconSize: 1,
              dynamicIconSize: true,
              dynamicIconSizeFactor: 0.2,
              iconSizeByFilter: {},
              iconSizeByGroup: {},
            });
          },

          resetInterface: () => {
            updateSettings({
              // Icon sizes & transforms
              transforms: {},
              mapTransform: null,
              playerIconSize: 1,
              heightArrowSize: 1,
              playerIconColor: "",
              playerBelowMarkers: false,
              baseIconSize: 1,
              dynamicIconSize: true,
              dynamicIconSizeFactor: 0.2,
              iconSizeByFilter: {},
              iconSizeByGroup: {},
              gridLabelSize: 1,
              // Accessibility
              colorBlindMode: "none",
              colorBlindSeverity: 1,
              mapDarkness: 0,
              highContrastMode: false,
              highContrastColor: "#FFFFFFCC",
              highContrastThickness: 2,
              // Trace line
              showTraceLine: true,
              followPlayer: true,
              rotateMapWithPlayer: false,
              rotateMapWithPlayerOverlay: false,
              rotateMapWithCtrlDrag: true,
              traceLineLength: 100,
              traceLineRate: 5,
              traceLineColor: "#1ccdd1B3",
              traceLineStyle: "dots",
              // Audio alerts
              audioAlertsMuted: false,
              audioAlertNotifications: false,
              audioAlertRange: 5000,
              audioAlertSound: "chime",
              audioAlertVolume: 0.5,
              audioAlertPositional: false,
              showAudioAlertRange: false,
              audioAlertByFilter: {},
              audioAlertSoundByFilter: {},
              // Labels
              labelModeByFilter: {},
              liveModeByFilter: {},
              discoverModeByFilter: {},
              labelTextSize: 1,
              // Map behavior
              fitBoundsOnChange: false,
              // Overlay window: a fullscreen or locked minimap is the usual
              // reason people reach for "reset" in the first place.
              overlayFullscreen: false,
              compactOverlay: false,
              compactOverlayTransform: null,
              lockedWindow: false,
            });
          },

          isDiscoveredNode: (nodeId) => {
            const state = get();
            const discoveredNodes = state.discoveredNodes;

            // Invalidate cache and rebuild the shared lookup if discoveredNodes
            // changed. Matching (exact / base-id / coordinate-with-tolerance)
            // lives in coordinates.ts so this selector, the marker render path,
            // and the discover/undiscover writes all agree.
            if (cachedDiscoveredNodes !== discoveredNodes) {
              cachedDiscoveredNodes = discoveredNodes;
              discoveredCache = new Map();
              discoveryLookup = buildDiscoveryLookup(discoveredNodes);
            }

            // Return cached result if available
            const cached = discoveredCache!.get(nodeId);
            if (cached !== undefined) {
              return cached;
            }

            const result = checkNodeDiscovered(nodeId, discoveryLookup!);
            discoveredCache!.set(nodeId, result);
            return result;
          },

          isAutoDiscoveredNode: (nodeId) => {
            const state = get();
            const autoDiscoveredNodes = state.autoDiscoveredNodes;
            if (autoDiscoveredNodes.length === 0) return false;
            if (cachedAutoDiscoveredNodes !== autoDiscoveredNodes) {
              cachedAutoDiscoveredNodes = autoDiscoveredNodes;
              autoDiscoveredCache = new Map();
              autoDiscoveryLookup = buildDiscoveryLookup(autoDiscoveredNodes);
            }
            const cached = autoDiscoveredCache!.get(nodeId);
            if (cached !== undefined) return cached;
            const result = checkNodeDiscovered(nodeId, autoDiscoveryLookup!);
            autoDiscoveredCache!.set(nodeId, result);
            return result;
          },

          toggleDiscoveredNode: (nodeId: string) => {
            const state = get();
            const discoveredNodes = state.discoveredNodes;
            const isDiscovered = state.isDiscoveredNode(nodeId);

            // Parse nodeId once for coordinate matching
            const nodeCoords = nodeId.includes("@")
              ? nodeId.slice(nodeId.indexOf("@") + 1)
              : null;

            const updatedNodes = isDiscovered
              ? discoveredNodes.filter((id) => {
                  // Exact match
                  if (id === nodeId) {
                    return false;
                  }
                  // Base ID match (type without coordinates)
                  if (nodeId.includes("@") && nodeId.split("@")[0] === id) {
                    return false;
                  }
                  // Coordinate match (backward compat + tolerance) so
                  // undiscovering matches a node stored at a slightly different
                  // float (live memory read vs static extracted coords).
                  if (nodeCoords && id.includes("@")) {
                    const idCoords = id.slice(id.indexOf("@") + 1);
                    if (coordsMatch(idCoords, nodeCoords)) {
                      return false;
                    }
                  }
                  return true;
                })
              : [...new Set([...discoveredNodes, nodeId])];

            updateSettings({ discoveredNodes: updatedNodes });
          },

          setDiscoverNode: (nodeId, discovered) => {
            const state = get();

            // Parse nodeId once for coordinate matching
            const nodeCoords = nodeId.includes("@")
              ? nodeId.slice(nodeId.indexOf("@") + 1)
              : null;

            updateSettings({
              discoveredNodes: discovered
                ? [...new Set([...state.discoveredNodes, nodeId])]
                : state.discoveredNodes.filter((id) => {
                    // Exact match
                    if (id === nodeId) {
                      return false;
                    }
                    // Base ID match (type without coordinates)
                    if (nodeId.includes("@") && nodeId.split("@")[0] === id) {
                      return false;
                    }
                    // Coordinate match (backward compat + tolerance) so
                    // undiscovering matches a node stored at a slightly
                    // different float (live memory read vs static coords).
                    if (nodeCoords && id.includes("@")) {
                      const idCoords = id.slice(id.indexOf("@") + 1);
                      if (coordsMatch(idCoords, nodeCoords)) {
                        return false;
                      }
                    }
                    return true;
                  }),
            });
          },

          toggleHideDiscoveredNodes: () => {
            const state = get();
            updateSettings({
              hideDiscoveredNodes: !state.hideDiscoveredNodes,
            });
          },

          setDiscoveredNodes: (discoveredNodes: string[]) => {
            updateSettings({ discoveredNodes });
          },

          setDiscoveredNodesBulk: (nodeIds: string[], discovered: boolean) => {
            const state = get();
            if (discovered) {
              updateSettings({
                discoveredNodes: [
                  ...new Set([...state.discoveredNodes, ...nodeIds]),
                ],
              });
              return;
            }
            updateSettings({
              discoveredNodes: removeDiscoveredMatches(
                state.discoveredNodes,
                nodeIds,
              ),
              autoDiscoveredNodes: removeDiscoveredMatches(
                state.autoDiscoveredNodes,
                nodeIds,
              ),
            });
          },

          markAutoDiscovered: (nodeIds: string[]) => {
            const state = get();
            updateSettings({
              discoveredNodes: [
                ...new Set([...state.discoveredNodes, ...nodeIds]),
              ],
              autoDiscoveredNodes: [
                ...new Set([...state.autoDiscoveredNodes, ...nodeIds]),
              ],
            });
          },

          setAutoDiscoverCollected: (enabled: boolean) => {
            const state = get();
            if (enabled) {
              updateSettings({ autoDiscoverCollected: true });
              return;
            }
            // Opting out un-discovers exactly what auto-discovery added (tracked in
            // autoDiscoveredNodes) so those nodes reappear, and clears the tag set.
            // Manually-discovered nodes are untouched.
            const auto = new Set(state.autoDiscoveredNodes);
            updateSettings({
              autoDiscoverCollected: false,
              discoveredNodes: state.discoveredNodes.filter(
                (id) => !auto.has(id),
              ),
              autoDiscoveredNodes: [],
            });
          },

          setAutoResetRespawned: (enabled: boolean) => {
            updateSettings({ autoResetRespawned: enabled });
          },

          setFlashDespawningNodes: (enabled: boolean) => {
            updateSettings({ flashDespawningNodes: enabled });
          },

          setPalCaptureBadges: (enabled: boolean) => {
            updateSettings({ palCaptureBadges: enabled });
          },

          setPalCaptureOnlyIncomplete: (enabled: boolean) => {
            updateSettings({ palCaptureOnlyIncomplete: enabled });
          },

          setPalCaptureCounts: (counts: Record<string, number> | null) => {
            updateSettings({ palCaptureCounts: counts });
          },

          setActorsPollingRate: (actorsPollingRate: number) => {
            updateSettings({ actorsPollingRate });
          },

          toggleShowTraceLine: () => {
            const state = get();
            updateSettings({
              showTraceLine: !state.showTraceLine,
            });
          },

          toggleFollowPlayer: () => {
            const state = get();
            updateSettings({
              followPlayer: !state.followPlayer,
            });
          },

          setRotateMapWithPlayer: (
            enabled: boolean,
            surface: RotateMapSurface,
          ) => {
            const state = get();
            updateSettings({
              [surface === "overlay"
                ? "rotateMapWithPlayerOverlay"
                : "rotateMapWithPlayer"]: enabled,
              ...(enabled && !state.followPlayer ? { followPlayer: true } : {}),
            });
          },

          setRotateMapWithCtrlDrag: (enabled: boolean) => {
            updateSettings({ rotateMapWithCtrlDrag: enabled });
          },

          setTraceLineLength: (traceLineLength: number) => {
            updateSettings({ traceLineLength });
          },

          setTraceLineRate: (traceLineRate: number) => {
            updateSettings({ traceLineRate });
          },

          setTraceLineColor: (traceLineColor: string) => {
            updateSettings({ traceLineColor });
          },

          setTraceLineStyle: (traceLineStyle: "dots" | "line") => {
            updateSettings({ traceLineStyle });
          },

          toggleAudioAlertsMuted: () => {
            const state = get();
            updateSettings({ audioAlertsMuted: !state.audioAlertsMuted });
          },

          toggleAudioAlertNotifications: () => {
            const state = get();
            updateSettings({
              audioAlertNotifications: !state.audioAlertNotifications,
            });
          },

          setAudioAlertRange: (range: number) => {
            updateSettings({ audioAlertRange: range });
          },

          setAudioAlertSound: (sound) => {
            updateSettings({ audioAlertSound: sound });
          },

          setAudioAlertVolume: (volume: number) => {
            updateSettings({
              audioAlertVolume: Math.max(0, Math.min(1, volume)),
            });
          },

          toggleAudioAlertPositional: () => {
            const state = get();
            updateSettings({
              audioAlertPositional: !state.audioAlertPositional,
            });
          },

          toggleShowAudioAlertRange: () => {
            const state = get();
            updateSettings({ showAudioAlertRange: !state.showAudioAlertRange });
          },

          togglePaliaEventAlert: (eventId: string) => {
            const state = get();
            updateSettings({
              paliaEventAlerts: {
                ...state.paliaEventAlerts,
                [eventId]: !(state.paliaEventAlerts?.[eventId] ?? false),
              },
            });
          },

          setPaliaEventAlertLeadMinutes: (minutes: number) => {
            updateSettings({
              paliaEventAlertLeadMinutes: Math.max(0, Math.min(30, minutes)),
            });
          },

          togglePaliaEventAlertsSpoken: () => {
            const state = get();
            updateSettings({
              paliaEventAlertsSpoken: !state.paliaEventAlertsSpoken,
            });
          },

          toggleAudioAlertByFilter: (filterId: string) => {
            const state = get();
            const current = state.audioAlertByFilter[filterId] ?? false;
            updateSettings({
              audioAlertByFilter: {
                ...state.audioAlertByFilter,
                [filterId]: !current,
              },
            });
          },

          setAudioAlertByFilters: (filterIds: string[], enabled: boolean) => {
            const state = get();
            const updates = filterIds.reduce(
              (acc, id) => {
                acc[id] = enabled;
                return acc;
              },
              {} as Record<string, boolean>,
            );
            updateSettings({
              audioAlertByFilter: { ...state.audioAlertByFilter, ...updates },
            });
          },

          setAudioAlertSoundByFilters: (filterIds, sound) => {
            const state = get();
            const next = { ...state.audioAlertSoundByFilter };
            for (const id of filterIds) {
              if (sound === "default") delete next[id];
              else next[id] = sound;
            }
            updateSettings({ audioAlertSoundByFilter: next });
          },

          resetAudioAlerts: () => {
            // Clear every per-filter audio alert back to off. Distinct from the
            // global mute (audioAlertsMuted), which only silences without
            // changing the per-filter toggles. The tone overrides go with them:
            // "Reset all" must not leave orphan tones behind.
            updateSettings({
              audioAlertByFilter: {},
              audioAlertSoundByFilter: {},
            });
          },

          setDiscoverModeByFilter: (filterId, mode) => {
            const state = get();
            const next = { ...state.discoverModeByFilter };
            if (mode === "default") {
              delete next[filterId];
            } else {
              next[filterId] = mode;
            }
            updateSettings({ discoverModeByFilter: next });
          },

          setDiscoverModeByFilters: (filterIds, mode) => {
            const state = get();
            const next = { ...state.discoverModeByFilter };
            for (const id of filterIds) {
              if (mode === "default") {
                delete next[id];
              } else {
                next[id] = mode;
              }
            }
            updateSettings({ discoverModeByFilter: next });
          },

          setLabelModeByFilter: (filterId: string, mode: LabelMode) => {
            const state = get();
            updateSettings({
              labelModeByFilter: {
                ...state.labelModeByFilter,
                [filterId]: mode,
              },
            });
          },

          setLabelModeByFilters: (filterIds: string[], mode: LabelMode) => {
            const state = get();
            const updates = filterIds.reduce(
              (acc, id) => {
                acc[id] = mode;
                return acc;
              },
              {} as Record<string, LabelMode>,
            );
            updateSettings({
              labelModeByFilter: { ...state.labelModeByFilter, ...updates },
            });
          },

          setLiveModeByFilter: (filterId, mode) => {
            const state = get();
            const next = { ...state.liveModeByFilter };
            if (mode === "default") {
              delete next[filterId];
            } else {
              next[filterId] = mode;
            }
            updateSettings({ liveModeByFilter: next });
          },

          setLiveModeByFilters: (filterIds, mode) => {
            const state = get();
            const next = { ...state.liveModeByFilter };
            for (const id of filterIds) {
              if (mode === "default") {
                delete next[id];
              } else {
                next[id] = mode;
              }
            }
            updateSettings({ liveModeByFilter: next });
          },

          setHideOverlayOnMap: (mapName, hide) => {
            const state = get();
            // `?? {}` guards profiles persisted before this field existed.
            const next = { ...(state.hideOverlayByMap ?? {}) };
            if (hide) {
              next[mapName] = true;
            } else {
              delete next[mapName];
            }
            updateSettings({ hideOverlayByMap: next });
          },

          setHideOverlayWithoutMap: (hide) => {
            updateSettings({ hideOverlayWithoutMap: hide });
          },

          setShowInteriorLabelNames: (show) => {
            updateSettings({ showInteriorLabelNames: show });
          },

          setLabelTextSize: (size: number) => {
            updateSettings({ labelTextSize: size });
          },

          setShowLabelsHotkey: (key: string) => {
            updateSettings({ showLabelsHotkey: key });
          },

          setDisplayDiscordActivityStatus: (
            displayDiscordActivityStatus: boolean,
          ) => {
            updateSettings({ displayDiscordActivityStatus });
          },

          setWorldCodeRequestsMuted: (muted: boolean) => {
            updateSettings({ worldCodeRequestsMuted: muted });
          },

          addPreset: (presetName: string, preset: FilterPreset) => {
            const state = get();
            updateSettings({
              presets: {
                ...state.presets,
                [presetName]: preset,
              },
            });
          },

          removePreset: (presetName: string) => {
            const state = get();
            const newPresets = { ...state.presets };
            delete newPresets[presetName];
            // A deleted preset must not stay bound to any map.
            updateSettings({
              presets: newPresets,
              presetByMap: dropPresetBindings(state.presetByMap, presetName),
            });
          },

          reorderPresets: (orderedNames: string[]) => {
            const state = get();
            const next: Record<string, string[] | FilterPreset> = {};
            for (const name of orderedNames) {
              if (name in state.presets) next[name] = state.presets[name];
            }
            // Keep any preset not in the list (defensive) at the end.
            for (const name of Object.keys(state.presets)) {
              if (!(name in next)) next[name] = state.presets[name];
            }
            updateSettings({ presets: next });
          },

          setPresetForMap: (mapName, presetName) => {
            const state = get();
            // `?? {}` (inside bindPresetToMap) guards profiles persisted
            // before this field existed.
            updateSettings({
              presetByMap: bindPresetToMap(
                state.presetByMap,
                mapName,
                presetName,
              ),
            });
          },

          applyPresetSettings: (settings) => {
            const update: Partial<ProfileSettings> = {};
            if (settings.iconSizes) {
              update.baseIconSize = settings.iconSizes.baseIconSize;
              update.iconSizeByGroup = settings.iconSizes.iconSizeByGroup;
              update.iconSizeByFilter = settings.iconSizes.iconSizeByFilter;
            }
            if (settings.audioAlertByFilter) {
              update.audioAlertByFilter = settings.audioAlertByFilter;
              // A preset replaces the per-filter alert map wholesale, so the
              // per-filter tones start over too: a tone must not linger on a
              // filter the preset just disabled. Presets carry no tones of
              // their own on purpose.
              update.audioAlertSoundByFilter = {};
            }
            updateSettings(update);
          },

          setTempPrivateNode: (tempPrivateNode) => {
            const state = get();
            updateSettings({
              tempPrivateNode: tempPrivateNode
                ? {
                    ...(state.tempPrivateNode ?? {}),
                    ...tempPrivateNode,
                  }
                : null,
            });
          },

          pushRecentPrivateNodeStyle: (style) => {
            const state = get();
            const signature = (s: PrivateNodeStyle) =>
              JSON.stringify({
                filter: s.filter ?? "",
                name: s.name ?? "",
                description: s.description ?? "",
                color: s.color ?? "",
                icon: s.icon ?? null,
                radius: s.radius,
              });
            const sig = signature(style);
            // Move an identical style to the front instead of duplicating it.
            const deduped = (state.recentPrivateNodeStyles ?? []).filter(
              (s) => signature(s) !== sig,
            );
            updateSettings({
              recentPrivateNodeStyles: [style, ...deduped].slice(
                0,
                MAX_RECENT_PRIVATE_NODE_STYLES,
              ),
            });
          },

          setTempPrivateDrawing: (tempPrivateDrawing) => {
            const state = get();
            updateSettings({
              tempPrivateDrawing: tempPrivateDrawing
                ? {
                    ...(state.tempPrivateDrawing ?? {}),
                    ...tempPrivateDrawing,
                  }
                : null,
            });
          },

          setDrawingColor: (drawingColor) => {
            updateSettings({ drawingColor });
          },

          setDrawingFillColor: (drawingFillColor) => {
            updateSettings({ drawingFillColor });
          },

          setDrawingSize: (drawingSize) => {
            updateSettings({ drawingSize });
          },

          setTextColor: (textColor) => {
            updateSettings({ textColor });
          },

          setTextSize: (textSize) => {
            updateSettings({ textSize });
          },

          setBaseIconSize: (baseIconSize) => {
            updateSettings({ baseIconSize });
          },

          toggleDynamicIconSize: () => {
            updateSettings({ dynamicIconSize: !get().dynamicIconSize });
          },

          setDynamicIconSizeFactor: (dynamicIconSizeFactor) => {
            updateSettings({
              dynamicIconSizeFactor: Math.max(
                0,
                Math.min(1, dynamicIconSizeFactor),
              ),
            });
          },

          setPlayerIconSize: (playerIconSize) => {
            updateSettings({ playerIconSize });
          },

          setHeightArrowSize: (heightArrowSize) => {
            updateSettings({
              heightArrowSize: Math.max(0.5, Math.min(3, heightArrowSize)),
            });
          },

          setPlayerIconColor: (playerIconColor) => {
            updateSettings({ playerIconColor });
          },

          togglePlayerBelowMarkers: () => {
            updateSettings({ playerBelowMarkers: !get().playerBelowMarkers });
          },

          setIconSizeByGroup: (group, size) => {
            const state = get();
            updateSettings({
              iconSizeByGroup: {
                ...state.iconSizeByGroup,
                [group]: size,
              },
            });
          },

          setIconSizeByFilter: (id, size) => {
            const state = get();
            updateSettings({
              iconSizeByFilter: {
                ...state.iconSizeByFilter,
                [id]: size,
              },
            });
          },

          toggleFitBoundsOnChange: () => {
            const state = get();
            updateSettings({
              fitBoundsOnChange: !state.fitBoundsOnChange,
            });
          },

          setMyFilters: (myFilters) => {
            updateSettings({ myFilters });
          },

          setMyFilter: (name, myFilter) => {
            const state = get();
            // applyFilterPatch returns the updated filters themselves, so the
            // upload can't be skipped by re-finding them under a key the patch
            // just moved — which is what silently dropped every rename.
            const { filters, updated } = applyFilterPatch(
              state.myFilters,
              name,
              myFilter,
            );
            if (updated.length === 0) return;
            updateSettings({ myFilters: filters });
            for (const filter of updated) scheduleFilterSync(filter);
          },

          addMyFilter: async (myFilter) => {
            // A freshly added/imported/adopted filter is not yet confirmed on
            // the server (its PUT hasn't succeeded), so it must not carry a
            // stale `synced` flag — otherwise a racing hydrate could treat it
            // as "deleted elsewhere" and drop it. The PUT success sets it true.
            if (myFilter.synced) myFilter = { ...myFilter, synced: false };
            // For signed-in users without a server id yet, assign one
            // locally so subsequent edits can sync. The first PUT will
            // create the row server-side via the upsert.
            if (isSignedIn() && !myFilter.id) {
              myFilter = { ...myFilter, id: crypto.randomUUID() };
            }
            if (isSignedIn() && !myFilter.game) {
              const inferred = getCurrentGameId();
              if (inferred) myFilter = { ...myFilter, game: inferred };
            }

            const state = get();
            // Dedupe by server id when available (importing a public
            // filter twice = no-op).
            if (
              myFilter.id &&
              state.myFilters.some((f) => f.id === myFilter.id)
            ) {
              return;
            }

            // A deliberate (re-)add wins over any earlier delete of the same
            // name/id — without this, a tombstone would eat the new filter on
            // the next rehydrate.
            clearFilterTombstones(myFilter);

            updateSettings({
              myFilters: [...state.myFilters, myFilter],
            });
            scheduleFilterSync(myFilter);
          },

          removeMyFilter: (myFilterName: string) => {
            const state = get();
            // Duplicate names are reachable (e.g. the same filter uploaded
            // under two ids by different surfaces) — tombstone and
            // server-delete EVERY match, not just the first.
            const { filters, removed } = removeFiltersMatching(
              state.myFilters,
              (f) => f.name === myFilterName,
            );
            if (removed.length === 0) return;
            for (const filter of removed) recordFilterTombstone(filter);
            updateSettings({ myFilters: filters });
            for (const filter of removed) {
              if (filter.id) fireFilterDelete(filter.id);
            }
          },

          resetMyFilters: () => {
            const state = get();
            // The settings "Reset" button used to call setMyFilters([]), which
            // clears the array and nothing else: no tombstones, no server
            // deletes. For a signed-in user the rows survived and the very
            // next hydrate resurrected everything just cleared. Reset is a
            // delete of every filter, so it goes through the same tombstone +
            // queued-DELETE path as removing one.
            const { filters, removed } = removeFiltersMatching(
              state.myFilters,
              () => true,
            );
            if (removed.length === 0) return;
            for (const filter of removed) recordFilterTombstone(filter);
            updateSettings({ myFilters: filters });
            for (const filter of removed) {
              if (filter.id) fireFilterDelete(filter.id);
            }
          },

          removeMyNode: async (nodeId: string) => {
            const state = get();
            const { filters, updated } = removeNodeFromFilters(
              state.myFilters,
              nodeId,
            );
            if (updated.length === 0) return;
            updateSettings({ myFilters: filters });
            for (const filter of updated) scheduleFilterSync(filter);
          },

          hydrateFiltersFromServer: async (game: string) => {
            if (!isSignedIn()) return;
            // Hydrate is the natural retry point for queued server deletes:
            // it runs on every signed-in mount/focus. Fire-and-forget — the
            // tombstone predicate below already hides anything still queued.
            void flushQueuedFilterDeletes();
            // Snapshot pending BEFORE the fetch: a filter whose PUT completes
            // DURING `apiListFilters` would otherwise be neither in the (stale)
            // server list nor still pending by the time we merge, and get
            // dropped as "deleted elsewhere". Unioning the before- and
            // after-fetch pending sets (plus the recently-synced grace inside
            // getPendingSyncIds) keeps such just-saved filters. This is the
            // "custom filter vanishes right after saving / on reopen" bug.
            const pendingBefore = getPendingSyncIds();
            let serverFilters;
            try {
              serverFilters = await apiListFilters(game);
            } catch (err) {
              console.error("[filter hydrate] failed", err);
              return;
            }
            const state = get();
            // Reconcile local vs server. Filters deleted on another device
            // (previously synced, now absent) are dropped so the deletion
            // sticks instead of being resurrected; anonymous / not-yet-uploaded
            // filters are preserved. See {@link mergeHydratedFilters}.
            const pendingIds = new Set<string>([
              ...pendingBefore,
              ...getPendingSyncIds(),
            ]);
            // Ids the server list DOES contain can't be deleted-elsewhere:
            // clear any stale hydrate-drop echo (a replica-lag false positive
            // or a re-creation) before it suppresses the filter in a sibling
            // window's union.
            clearHydrateDrops(serverFilters.map((f) => f.id));
            // Merge, adopt local-only filters, and work out what to re-push —
            // the composition that grew one union per 2026-09 regression fix;
            // see {@link planFilterHydrate} (tested as one unit).
            const { filters, droppedIds, toPush } = planFilterHydrate({
              local: state.myFilters,
              server: serverFilters.map(serverFilterToLocal),
              pendingIds,
              dirtyIds: getDirtyFilterIds(),
              isTombstoned: isFilterTombstoned,
              newId: () => crypto.randomUUID(),
              game,
            });
            // Broadcast deleted-elsewhere drops to sibling windows. A remote
            // delete records no local tombstone, so without this a sibling
            // still holding the filter in memory would union it back in on the
            // storage-event rehydrate that follows our persist below — the
            // filter would ping-pong instead of staying deleted.
            recordHydrateDrops(droppedIds);
            updateSettings({ myFilters: filters });
            for (const filter of toPush) scheduleFilterSync(filter);
          },

          adoptLocalOnlyFilters: () => {
            if (!isSignedIn()) return;
            const state = get();
            const { filters, adoptedIds } = adoptLocalFilters(
              state.myFilters,
              () => crypto.randomUUID(),
              getCurrentGameId() ?? undefined,
            );
            if (adoptedIds.length === 0) return;
            updateSettings({ myFilters: filters });
            const byId = new Map(filters.map((f) => [f.id, f]));
            for (const id of adoptedIds) {
              const filter = byId.get(id);
              if (filter) scheduleFilterSync(filter);
            }
          },

          toggleShowGrid: () => {
            const state = get();
            updateSettings({
              showGrid: !state.showGrid,
            });
          },

          setGridLabelSize: (gridLabelSize) => {
            updateSettings({ gridLabelSize });
          },

          toggleShowFilters: () => {
            const state = get();
            updateSettings({
              showFilters: !state.showFilters,
            });
          },

          // Peer Link / Mesh settings
          setPeerCode: (code: string) => {
            updateSettings({ peerCode: code });
          },

          setLastMeSenderId: (id: string) => {
            updateSettings({ lastMeSenderId: id });
          },

          setPlayerName: (name: string) => {
            updateSettings({ playerName: name });
          },

          setPlayerColor: (color: string) => {
            updateSettings({ playerColor: color });
          },

          setShowPeerLabels: (show: boolean) => {
            updateSettings({ showPeerLabels: show });
          },

          setAutoJoinPeer: (autoJoin: boolean) => {
            updateSettings({ autoJoinPeer: autoJoin });
          },

          setAutoLiveModeWithMe: (autoLiveMode: boolean) => {
            updateSettings({ autoLiveModeWithMe: autoLiveMode });
          },
        };
      },
      {
        name: getStorageName(),
        // Only persist profiles array and currentProfileId (not flattened
        // settings). Tombstoned filters are stripped on the way out so a
        // window holding stale in-memory state can't write a deleted filter
        // back into storage.
        partialize: (state) =>
          ({
            profiles: stripTombstonedFromProfiles(state.profiles),
            currentProfileId: state.currentProfileId,
          }) as SettingsStore,
        merge: (persistedState, currentState) => {
          // Merge persisted profiles and currentProfileId with current state
          const merged = {
            ...currentState,
            ...(persistedState as any),
          };

          // Flatten current profile settings to root level during merge.
          // Tombstoned filters are stripped first: rehydrate (mount AND
          // cross-window storage events) is where a resurrected blob written
          // by a stale window gets cleaned before it reaches the UI.
          // The reconcile order (strip tombstones → union in-memory adds on
          // the same profile, minus deleted-elsewhere drops → dedupe twins →
          // repair botched imports → write back into the profile) is what the
          // 2026-09 My Filters regressions kept breaking; it lives, tested, in
          // {@link reconcileRehydratedProfiles}.
          if (merged.profiles?.length) {
            const { profiles, currentProfile } = reconcileRehydratedProfiles({
              profiles: merged.profiles,
              currentProfileId: merged.currentProfileId,
              inMemory: currentState as {
                myFilters?: DrawingsAndNodes[];
                currentProfileId?: string;
              },
              isTombstoned: isFilterTombstoned,
              isRecentHydrateDrop,
            });
            merged.profiles = profiles;
            if (currentProfile) Object.assign(merged, currentProfile.settings);
          }
          // Profiles saved before a hotkey existed lack its key: give them the
          // default binding. Only for hotkeys added after clearing started to
          // persist as "" — older clears were stored as undefined (dropped by
          // JSON), so a missing older key may be a deliberate clear.
          if (merged.hotkeys) {
            for (const key of HOTKEYS_WITH_BACKFILLED_DEFAULT) {
              if (!(key in merged.hotkeys)) {
                merged.hotkeys = {
                  ...merged.hotkeys,
                  [key]: DEFAULT_PROFILE_SETTINGS.hotkeys[key],
                };
              }
            }
          }

          return merged;
        },
        onRehydrateStorage: () => (state) => {
          if (!state) return;

          if (!state._hasHydrated) {
            state.setHasHydrated(true);
          }
        },
        version: 5,
        // @ts-ignore
        migrate: (persistedState, version) => {
          if (version < 3) {
            const storageName = getStorageName();
            if (storageName !== "settings-storage") {
              const oldStorage = localStorage.getItem("settings-storage");
              if (oldStorage) {
                const oldState = JSON.parse(oldStorage).state;
                Object.assign(persistedState || {}, oldState);
              }
            }
          }

          if (version < 4) {
            // Initialize profiles from existing settings
            const state = persistedState as any;

            // Create or update the default profile with existing settings
            let defaultProfile: Profile;
            let isNewProfile = false;

            if (!state.profiles?.length) {
              // No profiles exist - create new one
              defaultProfile = ProfileManager.getDefaultProfile();
              isNewProfile = true;
            } else {
              // Profiles exist - use the current one or first one
              const currentProfileId =
                state.currentProfileId || state.profiles[0]?.id;
              defaultProfile =
                state.profiles.find(
                  (p: Profile) => p.id === currentProfileId,
                ) || state.profiles[0];
            }

            // Preserve existing settings from version 3 root level
            Object.keys(DEFAULT_PROFILE_SETTINGS).forEach((key) => {
              if (key in state && state[key] !== undefined) {
                // @ts-ignore
                defaultProfile.settings[key] = state[key];
              }
            });

            if (isNewProfile) {
              state.profiles = [defaultProfile];
              state.currentProfileId = defaultProfile.id;
            } else {
              // Update the existing profile in the array
              state.profiles = state.profiles.map((p: Profile) =>
                p.id === defaultProfile.id ? defaultProfile : p,
              );
              state.currentProfileId = defaultProfile.id;
            }
          }

          if (version < 5) {
            // liveMode boolean → tri-state: true→'combined', false→'static'
            const state = persistedState as any;
            const convert = (v: unknown): LiveMode =>
              typeof v === "string"
                ? (v as LiveMode)
                : v
                  ? "combined"
                  : "static";
            if ("liveMode" in state) {
              state.liveMode = convert(state.liveMode);
            }
            if (Array.isArray(state.profiles)) {
              state.profiles = state.profiles.map((p: Profile) => ({
                ...p,
                settings: {
                  ...p.settings,
                  liveMode: convert(p.settings?.liveMode),
                },
              }));
            }
          }

          return persistedState;
        },
      },
    ),
  ),
);

withStorageDOMEvents(useSettingsStore);
